/**
 * Calendar Upload Module
 *
 * Month-slot photo upload for Calendar Photo Magnets.
 * Manages a 12-slot January–December grid with:
 * - Uploadcare multi-file upload with auto-assignment to months
 * - SortableJS drag-and-drop swapping between months
 * - Per-month replace (reuses main uploader) and remove
 * - Auto-fill: fewer than 12 photos are repeated evenly
 * - localStorage persistence across page refreshes
 *
 * Exposes:
 *   window.CalendarPhotos  — final 12-entry month map for product-form.js
 *   window.CalendarUpload  — API: { isValid, getUniqueCount, getMonthPhotos }
 */
(function () {
  'use strict';

  // =========================================================================
  // Constants
  // =========================================================================
  var MONTHS = [
    'january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december'
  ];

  var STORAGE_KEY = 'hmp_calendar_uploads';
  var TOTAL_MONTHS = 12;

  // =========================================================================
  // State
  // =========================================================================

  /**
   * Canonical state — 12 entries, one per month.
   * Each entry is null or { uuid, cdnUrl, filename, internalId, source }
   * source: 'uploaded' (user placed) | 'auto' (repeated from another month)
   */
  var monthSlots = {};
  MONTHS.forEach(function (m) { monthSlots[m] = null; });

  // Track which month is being replaced (set before opening the uploader)
  var replaceTargetMonth = null;

  // =========================================================================
  // DOM References
  // =========================================================================
  var container = document.querySelector('[data-calendar-upload]');
  if (!container) return;

  var grid = container.querySelector('[data-calendar-grid]');
  var counterEl = container.querySelector('[data-calendar-counter]');
  var dragHint = container.querySelector('[data-calendar-drag-hint]');
  var fullNoteEl = container.querySelector('[data-calendar-full-note]');
  var uploaderEl = container.querySelector('.calendar-upload__widget');
  var uploaderConfigEl = container.querySelector('uc-config[ctx-name="calendar-uploader"]');

  // Uploadcare context provider (main uploader — also used for replacements)
  var mainCtx = document.getElementById('calendar-upload-ctx');
  var isReplaceFlow = false;

  // =========================================================================
  // Initialization
  // =========================================================================
  function init() {
    // Restore from localStorage if available
    restoreFromStorage();

    // Ensure uploader is configured for normal multi-file adds.
    setUploaderLimits({ max: TOTAL_MONTHS, multiple: true });

    // Wait for Uploadcare custom elements to be defined
    if (mainCtx) {
      customElements.whenDefined('uc-upload-ctx-provider').then(function () {
        mainCtx.addEventListener('file-upload-success', handleMainUpload);
        mainCtx.addEventListener('file-removed', handleMainRemoved);

        // When the user edits a photo (brightness/filters/crop) and clicks Apply,
        // Uploadcare updates the entry's cdnUrl (with modifiers). We must sync
        // the edited URL back into our monthSlots, otherwise we keep the original.
        document.addEventListener('apply', handleEditorApply, true);

        // Reset replace target if user closes the dialog without uploading
        mainCtx.addEventListener('modal-close', function (e) {
          var detail = e.detail || {};
          if (!detail.hasActiveModals) {
            replaceTargetMonth = null;
            isReplaceFlow = false;
            setUploaderLimits({ max: TOTAL_MONTHS, multiple: true });
          }
        });

        // Set locale overrides for the calendar uploader
        customElements.whenDefined('uc-config').then(function () {
          var cfg = document.querySelector('uc-config[ctx-name="calendar-uploader"]');
          if (cfg) {
            cfg.localeDefinitionOverride = {
              en: {
                'upload-file': 'Add photos',
                'upload-files': 'Add photos',
                'drop-files-here': 'Drop photos here',
              },
            };
          }
        });
      });
    }

    // Bind action buttons (Replace / Remove)
    grid.addEventListener('click', handleGridClick);

    // Initialize SortableJS when available
    initSortable();

    // Update dynamic max on the Uploadcare widget
    updateUploadMax();

    // Initial render
    renderGrid();
    updateProgress();
    exposeAPI();
  }

  // =========================================================================
  // Uploadcare Event Handlers
  // =========================================================================

  /**
   * Handle a file uploaded via the main (multi-file) uploader.
   * If replaceTargetMonth is set, the first file replaces that month;
   * otherwise auto-assigns to the next empty month slot.
   */
  function handleMainUpload(e) {
    var entry = e.detail;
    if (!entry || !entry.cdnUrl) return;

    var targetMonth;
    var priorInternalIdToRemove = null;

    // Replace mode: assign to the specific month being replaced
    if (replaceTargetMonth) {
      targetMonth = replaceTargetMonth;
      replaceTargetMonth = null; // clear after first file
      // If this month already had an uploaded file, remove the old entry from
      // Uploadcare's internal collection so the uploader's "file count" doesn't
      // grow over time when customers replace months.
      var prior = monthSlots[targetMonth];
      if (prior && prior.source === 'uploaded' && prior.internalId) {
        priorInternalIdToRemove = prior.internalId;
      }
    } else {
      // Normal mode: find next empty (non-uploaded) month
      targetMonth = null;
      for (var i = 0; i < MONTHS.length; i++) {
        var slot = monthSlots[MONTHS[i]];
        if (!slot || slot.source !== 'uploaded') {
          targetMonth = MONTHS[i];
          break;
        }
      }
    }

    if (!targetMonth) {
      // All months already have uploaded photos — ignore
      // Also remove the entry from Uploadcare's internal collection so its
      // "file count" can't drift past 12 (especially after a refresh when
      // localStorage has 12 months filled but Uploadcare starts empty).
      if (entry.internalId && mainCtx) {
        try {
          var apiIgnore = mainCtx.getAPI();
          if (apiIgnore && typeof apiIgnore.removeFileByInternalId === 'function') {
            apiIgnore.removeFileByInternalId(entry.internalId);
          }
        } catch (eIgnore) {
          // ignore
        }
      }
      return;
    }

    monthSlots[targetMonth] = {
      uuid: entry.uuid,
      cdnUrl: entry.cdnUrl,
      filename: entry.name || entry.uuid || 'photo',
      internalId: entry.internalId,
      source: 'uploaded'
    };

    // Remove prior file from uploader state (replace mode only).
    if (priorInternalIdToRemove && mainCtx) {
      try {
        var api = mainCtx.getAPI();
        if (api && typeof api.removeFileByInternalId === 'function') {
          api.removeFileByInternalId(priorInternalIdToRemove);
        }
      } catch (e2) {
        // Best-effort only; our monthSlots state is still correct.
      }
    }

    // Restore normal limits after a successful replacement.
    if (isReplaceFlow) {
      isReplaceFlow = false;
      setUploaderLimits({ max: TOTAL_MONTHS, multiple: true });
    }

    computeAutoFill();
    renderGrid();
    updateProgress();
    updateUploadMax();
    saveToStorage();
  }

  /**
   * Handle file removal from the main Uploadcare widget.
   */
  function handleMainRemoved(e) {
    var entry = e.detail;
    if (!entry) return;

    // Find and clear the month that has this internalId
    MONTHS.forEach(function (m) {
      var slot = monthSlots[m];
      if (slot && slot.source === 'uploaded' && slot.internalId === entry.internalId) {
        monthSlots[m] = null;
      }
    });

    computeAutoFill();
    renderGrid();
    updateProgress();
    updateUploadMax();
    saveToStorage();
  }

  /**
   * Cloud Image Editor "Apply" event handler (capture phase).
   * We delay syncing by a tick to give the uploader time to update its state.
   */
  function handleEditorApply(e) {
    var target = e && e.target;
    if (!target || target.tagName !== 'UC-CLOUD-IMAGE-EDITOR') return;
    setTimeout(syncEditedUrlsFromUploader, 0);
  }

  /**
   * Sync edited cdnUrl values from Uploadcare widget state back into monthSlots.
   * This ensures that filters/brightness edits show in our thumbnails AND are
   * submitted to cart via line item properties.
   */
  function syncEditedUrlsFromUploader() {
    if (!mainCtx) return;
    var api;
    try {
      api = mainCtx.getAPI();
    } catch (e) {
      return;
    }
    if (!api || typeof api.getOutputCollectionState !== 'function') return;

    var state;
    try {
      state = api.getOutputCollectionState();
    } catch (e2) {
      return;
    }
    if (!state || !state.allEntries || !state.allEntries.length) return;

    var byInternalId = {};
    state.allEntries.forEach(function (entry) {
      if (!entry || !entry.internalId || !entry.cdnUrl) return;
      byInternalId[entry.internalId] = entry;
    });

    var changed = false;
    MONTHS.forEach(function (m) {
      var slot = monthSlots[m];
      if (!slot || slot.source !== 'uploaded' || !slot.internalId) return;
      var entry = byInternalId[slot.internalId];
      if (!entry) return;

      if (entry.cdnUrl && entry.cdnUrl !== slot.cdnUrl) {
        slot.cdnUrl = entry.cdnUrl;
        changed = true;
      }
      if (entry.uuid && entry.uuid !== slot.uuid) {
        slot.uuid = entry.uuid;
        changed = true;
      }
      if ((entry.name || '') && entry.name !== slot.filename) {
        slot.filename = entry.name;
        changed = true;
      }
    });

    if (!changed) return;

    computeAutoFill();
    renderGrid();
    updateProgress();
    saveToStorage();
  }

  // =========================================================================
  // Grid Actions (Replace / Remove)
  // =========================================================================

  /**
   * Handle clicks on action buttons within the grid.
   */
  function handleGridClick(e) {
    var btn = e.target.closest('[data-action]');
    if (!btn) return;

    var action = btn.dataset.action;
    var month = btn.dataset.month;
    if (!month) return;

    if (action === 'remove') {
      removeMonth(month);
    } else if (action === 'replace') {
      startReplace(month);
    }
  }

  /**
   * Remove a photo from a specific month.
   */
  function removeMonth(month) {
    var slot = monthSlots[month];
    if (!slot) return;

    // If it was uploaded, also remove from Uploadcare widget state
    if (slot.source === 'uploaded' && slot.internalId && mainCtx) {
      try {
        var api = mainCtx.getAPI();
        if (api && typeof api.removeFileByInternalId === 'function') {
          api.removeFileByInternalId(slot.internalId);
        }
      } catch (e) {
        // Silently fail
      }
    }

    monthSlots[month] = null;

    computeAutoFill();
    renderGrid();
    updateProgress();
    updateUploadMax();
    saveToStorage();
  }

  /**
   * Start the replace flow for a specific month.
   * Reuses the main uploader — the replaceTargetMonth flag tells
   * handleMainUpload to assign the next photo to that month.
   */
  function startReplace(month) {
    replaceTargetMonth = month;
    isReplaceFlow = true;
    // In replace mode we only want ONE file selected.
    setUploaderLimits({ max: 1, multiple: false });

    // Open the main Uploadcare dialog via its official API.
    if (mainCtx) {
      try {
        var api = mainCtx.getAPI();
        if (api && typeof api.initFlow === 'function') {
          api.initFlow();
        }
      } catch (e) {
        console.warn('Calendar replace: could not open upload dialog', e);
        replaceTargetMonth = null;
        isReplaceFlow = false;
        setUploaderLimits({ max: TOTAL_MONTHS, multiple: true });
      }
    }
  }

  // =========================================================================
  // Auto-Fill Algorithm
  // =========================================================================

  /**
   * Distribute uploaded photos across empty months.
   * Uses the same even-repeat algorithm as the generic upload system.
   */
  function computeAutoFill() {
    // First, clear all auto-filled slots
    MONTHS.forEach(function (m) {
      if (monthSlots[m] && monthSlots[m].source === 'auto') {
        monthSlots[m] = null;
      }
    });

    // Collect uploaded photos in order
    var uploaded = [];
    MONTHS.forEach(function (m) {
      if (monthSlots[m] && monthSlots[m].source === 'uploaded') {
        uploaded.push({ month: m, photo: monthSlots[m] });
      }
    });

    if (uploaded.length === 0 || uploaded.length >= 12) return;

    // Find empty months
    var emptyMonths = [];
    MONTHS.forEach(function (m) {
      if (!monthSlots[m]) {
        emptyMonths.push(m);
      }
    });

    if (emptyMonths.length === 0) return;

    // Distribute uploaded photos evenly across empty months
    // Each uploaded photo gets assigned to roughly equal number of empty slots
    for (var i = 0; i < emptyMonths.length; i++) {
      var sourceIndex = i % uploaded.length;
      var sourcePhoto = uploaded[sourceIndex].photo;

      monthSlots[emptyMonths[i]] = {
        uuid: sourcePhoto.uuid,
        cdnUrl: sourcePhoto.cdnUrl,
        filename: sourcePhoto.filename,
        internalId: null, // auto-filled, not tracked in Uploadcare
        source: 'auto'
      };
    }
  }

  // =========================================================================
  // SortableJS Integration
  // =========================================================================

  /**
   * Initialize SortableJS on the month grid for drag-and-drop swapping.
   */
  function initSortable() {
    if (typeof Sortable === 'undefined') {
      // SortableJS not loaded yet — retry after a short delay
      setTimeout(initSortable, 200);
      return;
    }

    Sortable.create(grid, {
      animation: 150,
      handle: '.calendar-month__drag-handle',
      ghostClass: 'sortable-ghost',
      chosenClass: 'sortable-chosen',
      dragClass: 'sortable-drag',
      swapThreshold: 0.65,
      onEnd: function (evt) {
        var fromIndex = evt.oldIndex;
        var toIndex = evt.newIndex;

        if (fromIndex === toIndex) return;

        var fromMonth = MONTHS[fromIndex];
        var toMonth = MONTHS[toIndex];

        // Swap the slot data
        var temp = monthSlots[fromMonth];
        monthSlots[fromMonth] = monthSlots[toMonth];
        monthSlots[toMonth] = temp;

        // SortableJS already moved the DOM elements, but we need to
        // re-render to update data attributes and correct visual state
        // First, restore the DOM order to match MONTHS array
        restoreDomOrder();

        computeAutoFill();
        renderGrid();
        updateProgress();
        saveToStorage();
      }
    });
  }

  /**
   * Restore DOM element order to match the MONTHS array.
   * SortableJS physically moves elements, but our data model uses
   * month names (not DOM order). We re-sort DOM to keep them aligned.
   */
  function restoreDomOrder() {
    var children = Array.from(grid.children);
    children.sort(function (a, b) {
      var aMonth = a.dataset.calendarMonth;
      var bMonth = b.dataset.calendarMonth;
      return MONTHS.indexOf(aMonth) - MONTHS.indexOf(bMonth);
    });
    children.forEach(function (child) {
      grid.appendChild(child);
    });
  }

  // =========================================================================
  // Rendering
  // =========================================================================

  /**
   * Set an image src with broken-image fallback.
   * If the CDN URL fails to load, the slot is reset to empty so
   * stale localStorage data doesn't show broken thumbnails.
   */
  function setImageWithFallback(imgEl, emptyState, url, alt, month) {
    imgEl.src = url;
    imgEl.alt = alt;
    imgEl.removeAttribute('hidden');
    emptyState.style.display = 'none';

    imgEl.onerror = function () {
      // Image failed — reset this slot to empty
      imgEl.setAttribute('hidden', '');
      imgEl.src = '';
      emptyState.style.display = '';

      // Also reset the data model so the slot is properly empty
      if (month) {
        monthSlots[month] = null;
        var card = grid.querySelector('[data-calendar-month="' + month + '"]');
        if (card) {
          card.classList.remove('calendar-month--uploaded', 'calendar-month--autofill');
          card.classList.add('calendar-month--empty');
          var actionsEl = card.querySelector('.calendar-month__actions');
          var autofillBadge = card.querySelector('.calendar-month__autofill-badge');
          if (actionsEl) actionsEl.setAttribute('hidden', '');
          if (autofillBadge) autofillBadge.setAttribute('hidden', '');
        }
        saveToStorage();
        updateProgress();
      }
    };
  }

  /**
   * Render all 12 month slots based on current state.
   */
  function renderGrid() {
    MONTHS.forEach(function (month) {
      var el = grid.querySelector('[data-calendar-month="' + month + '"]');
      if (!el) return;

      var slot = monthSlots[month];
      var imgEl = el.querySelector('.calendar-month__image');
      var emptyState = el.querySelector('.calendar-month__empty-state');
      var autofillBadge = el.querySelector('.calendar-month__autofill-badge');
      var actionsEl = el.querySelector('.calendar-month__actions');
      var removeBtn = el.querySelector('[data-action="remove"]');

      // Reset classes
      el.classList.remove('calendar-month--empty', 'calendar-month--uploaded', 'calendar-month--autofill');

      if (!slot) {
        // Empty state
        el.classList.add('calendar-month--empty');
        imgEl.setAttribute('hidden', '');
        imgEl.src = '';
        imgEl.onerror = null;
        emptyState.style.display = '';
        autofillBadge.setAttribute('hidden', '');
        actionsEl.setAttribute('hidden', '');
      } else if (slot.source === 'uploaded') {
        // Uploaded state
        el.classList.add('calendar-month--uploaded');
        setImageWithFallback(imgEl, emptyState,
          slot.cdnUrl + '-/scale_crop/240x240/center/',
          slot.filename, month);
        autofillBadge.setAttribute('hidden', '');
        actionsEl.removeAttribute('hidden');
        if (removeBtn) removeBtn.removeAttribute('hidden');
      } else if (slot.source === 'auto') {
        // Auto-filled state
        el.classList.add('calendar-month--autofill');
        setImageWithFallback(imgEl, emptyState,
          slot.cdnUrl + '-/scale_crop/240x240/center/',
          slot.filename + ' (repeated)', month);
        autofillBadge.removeAttribute('hidden');
        actionsEl.removeAttribute('hidden');
        // Hide remove for auto-filled (they auto-recompute)
        if (removeBtn) removeBtn.setAttribute('hidden', '');
      }
    });

    // Update the exposed API
    exposeAPI();
  }

  /**
   * Update progress bar, counter text, and drag hint visibility.
   */
  function updateProgress() {
    var uniqueCount = getUniqueCount();

    // Hide the month grid until the customer has uploaded at least 1 photo.
    // (If state is restored from localStorage, uniqueCount will be > 0 and the
    // grid will render immediately on page load.)
    if (grid) {
      if (uniqueCount >= 1) {
        grid.removeAttribute('hidden');
      } else {
        grid.setAttribute('hidden', '');
      }
    }

    if (counterEl) {
      counterEl.textContent = uniqueCount + ' of 12';
      if (uniqueCount >= 12) {
        counterEl.classList.add('calendar-upload__counter--complete');
      } else {
        counterEl.classList.remove('calendar-upload__counter--complete');
      }
    }

    // Show drag instruction once at least 2 photos are uploaded (dragging makes sense)
    if (dragHint) {
      if (uniqueCount >= 2) {
        dragHint.removeAttribute('hidden');
      } else {
        dragHint.setAttribute('hidden', '');
      }
    }

    // Once all 12 unique uploads are set, hide the main uploader and require using "Replace".
    setUploaderEnabled(uniqueCount < TOTAL_MONTHS);
  }

  // =========================================================================
  // Uploadcare Dynamic Max
  // =========================================================================

  /**
   * Update the Uploadcare widget's multiple-max.
   *
   * IMPORTANT:
   * Uploadcare's `multiple-max` applies to the TOTAL number of files in the
   * uploader's internal collection, not "how many more you can add right now".
   * Since customers can add photos in multiple batches, setting `multiple-max`
   * to "remaining" would incorrectly block later batches (e.g. 5 already added,
   * remaining=7, selecting 3 more would fail because 5+3 > 7).
   *
   * So we keep this fixed at 12 (one per month).
   */
  function updateUploadMax() {
    // Keep the uploader cap fixed at 12 in normal mode.
    // Replace flow temporarily applies a stricter limit (1).
    if (isReplaceFlow) return;
    setUploaderLimits({ max: TOTAL_MONTHS, multiple: true });
  }

  // =========================================================================
  // Helpers
  // =========================================================================

  /**
   * Get count of uniquely uploaded (non-auto-filled) photos.
   */
  function getUniqueCount() {
    var count = 0;
    MONTHS.forEach(function (m) {
      if (monthSlots[m] && monthSlots[m].source === 'uploaded') {
        count++;
      }
    });
    return count;
  }

  /**
   * Get the final month-photo mapping for cart submission.
   * Returns an object with all 12 months, each having { cdnUrl, source }.
   */
  function getMonthPhotos() {
    var result = {};
    MONTHS.forEach(function (m) {
      result[m] = monthSlots[m] ? {
        cdnUrl: monthSlots[m].cdnUrl,
        source: monthSlots[m].source
      } : null;
    });
    return result;
  }

  /**
   * Validation: at least 1 photo uploaded.
   */
  function isValid() {
    return getUniqueCount() >= 1;
  }

  /**
   * Enable/disable the main uploader UI.
   * When disabled (12/12 unique months filled), customers can still change
   * photos via per-month "Replace" buttons.
   */
  function setUploaderEnabled(enabled) {
    if (uploaderEl) {
      uploaderEl.classList.toggle('calendar-upload__widget--disabled', !enabled);
      uploaderEl.setAttribute('aria-disabled', enabled ? 'false' : 'true');
    }
    if (fullNoteEl) fullNoteEl.toggleAttribute('hidden', enabled);
  }

  /**
   * Set Uploadcare uploader limits by adjusting <uc-config> attributes.
   */
  function setUploaderLimits(opts) {
    if (!uploaderConfigEl) return;
    var max = opts && typeof opts.max === 'number' ? opts.max : TOTAL_MONTHS;
    var multiple = opts && typeof opts.multiple === 'boolean' ? opts.multiple : true;
    uploaderConfigEl.setAttribute('multiple', multiple ? 'true' : 'false');
    uploaderConfigEl.setAttribute('multiple-max', String(max));
  }

  // =========================================================================
  // localStorage Persistence
  // =========================================================================

  /**
   * Save current state to localStorage.
   * Only saves uploaded photos (auto-filled are recomputed on restore).
   */
  function saveToStorage() {
    try {
      var data = {};
      MONTHS.forEach(function (m) {
        var slot = monthSlots[m];
        if (slot && slot.source === 'uploaded') {
          data[m] = {
            uuid: slot.uuid,
            cdnUrl: slot.cdnUrl,
            filename: slot.filename
          };
        }
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      // localStorage unavailable
    }
  }

  /**
   * Restore state from localStorage.
   */
  function restoreFromStorage() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;

      var data = JSON.parse(raw);
      if (!data || typeof data !== 'object') return;

      MONTHS.forEach(function (m) {
        if (data[m] && data[m].cdnUrl) {
          monthSlots[m] = {
            uuid: data[m].uuid || '',
            cdnUrl: data[m].cdnUrl,
            filename: data[m].filename || 'photo',
            internalId: null, // restored photos don't have widget internalIds
            source: 'uploaded'
          };
        }
      });

      computeAutoFill();
    } catch (e) {
      // Corrupted data — start fresh
    }
  }

  // =========================================================================
  // Public API
  // =========================================================================

  /**
   * Expose the final month-photo mapping on window for product-form.js.
   */
  function exposeAPI() {
    window.CalendarPhotos = getMonthPhotos();

    window.CalendarUpload = {
      isValid: isValid,
      getUniqueCount: getUniqueCount,
      getMonthPhotos: getMonthPhotos
    };
  }

  // =========================================================================
  // Boot
  // =========================================================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
